// Metadata only — the file bytes are never stored locally, see the note on
// messages_cache.attachments (lib/db). Fetch via
// GET /messages/{id}/attachments/{attachmentId}, which proxies live from
// Gmail/Outlook.
// `inline` (a signature/logo image referenced by cid: in the body, already
// rewritten there to this attachment's own download URL) is set-but-absent
// on a real attachment — filter these out of any attachment-list UI, since
// they're already shown inline rather than being a separate downloadable file.
export type MessageAttachment = { id: string; filename: string; mimeType: string; size: number; inline?: boolean };

export type Message = {
  id: string;
  source: string;
  fromName: string;
  fromEmail?: string | null;
  subject?: string | null;
  channel?: string | null;
  snippet: string;
  body?: string | null;
  timestamp: string;
  isRead: boolean;
  isPinned?: boolean;
  snoozedUntil?: string | null;
  archivedAt?: string | null;
  folderKey?: string | null;
  aiPriority?: string | null;
  aiSummary?: string | null;
  isMeetingInvite?: boolean;
  meetingInviteResponse?: "accepted" | "declined" | "tentative" | null;
  attachments?: MessageAttachment[];
  // Slack thread affordance — only ever set by the on-demand channel/thread
  // hydration endpoints, never by the main message list.
  threadTs?: string | null;
  replyCount?: number | null;
  // Teams reply identifiers — exactly one of chat vs team+channel is set,
  // never both. Needed to reply; see teams-reply's request shape.
  teamsChatId?: string | null;
  teamsTeamId?: string | null;
  teamsChannelId?: string | null;
  // Chat-provider enrichment (Slack/Teams). fromProviderUserId keys the
  // presence dot + proxied avatar for the sender; reactions/mentions/importance
  // decorate the bubble. Absent/empty on email + task rows.
  fromProviderUserId?: string | null;
  reactions?: { key: string; count: number }[];
  mentions?: string[];
  importance?: string | null;
};

export type GmailItem = { kind: "gmail"; id: string; msg: Message };
export type OutlookItem = { kind: "outlook"; id: string; msg: Message };
export type SlackItem = {
  kind: "slack";
  channel: string;
  messages: Message[]; // sorted newest-first
  latest: Message;
  unreadCount: number;
};
export type TeamsItem = {
  kind: "teams";
  channel: string;
  // "chat:{chatId}" or "channel:{teamId}:{channelId}" — stable, URL-safe
  // identifier for this conversation (channel/topic display labels aren't
  // stable/unique the way Slack's channel names are, so routing needs this
  // instead).
  conversationKey: string;
  messages: Message[]; // sorted newest-first
  latest: Message;
  unreadCount: number;
};
export type NativeChatItem = {
  kind: "native";
  channel: string;
  messages: Message[]; // sorted newest-first
  latest: Message;
  unreadCount: number;
};
export type ListItem = GmailItem | OutlookItem | SlackItem | TeamsItem | NativeChatItem;

export type SortMode = "newest" | "oldest" | "unread" | "priority";

// The common "type" filters most email clients offer, applied client-side
// on top of whatever the source (Gmail/Outlook/all) filter already fetched.
export type TypeFilter = "all" | "unread" | "starred" | "priority";
export const TYPE_FILTER_LABELS: Record<TypeFilter, string> = {
  all: "All",
  unread: "Unread",
  starred: "Starred",
  priority: "High priority",
};

// How much of each row a message row shows below the subject line — the
// subject itself always stays a single truncated line regardless of density.
export type EmailDensity = "compact" | "cozy" | "spacious";
export const EMAIL_DENSITY_LABELS: Record<EmailDensity, { label: string; description: string }> = {
  compact: { label: "Compact", description: "Subject only" },
  cozy: { label: "Cozy", description: "1 line preview" },
  spacious: { label: "Spacious", description: "3 line preview" },
};

// Shared by Inbox.tsx (reads it once on mount) and the Display settings tab
// (Settings.tsx — the actual control for changing it lives there now, not
// in the inbox itself).
export function loadEmailDensity(): EmailDensity {
  try {
    const saved = localStorage.getItem("assistio.inbox.density");
    if (saved === "compact" || saved === "cozy" || saved === "spacious") return saved;
  } catch {
    // localStorage unavailable — falls through to the default
  }
  return "cozy";
}

export const UNDO_WINDOW_MS = 5000;

export type PendingAction = {
  uid: string;
  label: string;
  queryKey: readonly unknown[];
  timer: ReturnType<typeof setTimeout>;
};

export type Folder = {
  key: string;
  kind: "domain" | "slack_channel" | "sent" | "list" | "provider_folder" | "teams_conversation" | "native_channel";
  label: string;
  isCustomLabel: boolean;
  messageCount: number;
  unreadCount: number;
  latestTimestamp: string;
  icon: string | null; // an emoji, only meaningful when kind === "list"
  source: string | null; // sync-owned origin — "jira"/"google_tasks" for "list", "gmail"/"outlook" for "provider_folder"
  color: string | null; // a TAG_COLOR_KEYS palette key, only meaningful when kind === "list"
  // The parent folder's own key — only meaningful when kind === "provider_folder"
  // (mirrors the account's real Gmail label tree / Outlook mail folder tree).
  // Null for a top-level provider folder and for every other kind, which are always flat.
  parentKey: string | null;
  // For a 1:1/group Slack or Teams chat, the other person (most recent non-"You"
  // sender) — drives the presence dot in the nav chat list. Null for channels
  // and non-chat folders.
  presenceUserId?: string | null;
  presenceSource?: "slack" | "teams" | null;
};

export type TaskItem = {
  id: number;
  listId: number;
  title: string;
  description: string | null;
  status: string; // "todo" | "in_progress" | "blocked" | "done"
  // The source system's own status/state name (e.g. Jira's "In Review", an
  // ADO work item's "Active") — read-only display info alongside `status`.
  // Null for native tasks; Jira/ADO workflows are per-project configurable,
  // so there's no fixed vocabulary to fold this into `status` itself.
  rawStatus: string | null;
  // Whatever `status` was right before it was last marked "done" — restored
  // when un-marking done, instead of always resetting to "todo".
  previousStatus: string | null;
  priorityTagId: number | null;
  parentTaskId: number | null; // one level deep only — a subtask never has its own subtasks
  position: number | null; // manual drag order; null falls back to computed (due-date) order
  dueDate: string | null;
  completedAt: string | null;
  source: string | null; // sync-owned origin, e.g. "jira" — null for native tasks
  externalId: string | null; // identity in the source system, e.g. a Jira issue key
  externalUrl: string | null; // link to the source record, e.g. the Jira issue
  isRecurring: boolean; // set when this task's time blocks were created as a recurring series
  recurrenceFrequency: string | null; // "daily" | "weekly" | "monthly" — null unless isRecurring
  recurrenceInterval: number | null; // every N of recurrenceFrequency's unit — null unless isRecurring
  createdAt: string;
  updatedAt: string;
};

export type TaskTag = {
  id: number;
  listId: number; // priority levels are per-list, not shared across the whole app
  name: string;
  color: string; // preset palette key, see TAG_COLOR_STYLES
  createdAt: string;
};

export type TaskStatus = {
  id: number;
  listId: number; // custom statuses are per-list, same convention as priority tags
  name: string;
  color: string; // preset palette key, see TAG_COLOR_STYLES
  createdAt: string;
};

export type TaskFieldType = "date" | "text" | "textarea" | "select";

export type TaskField = {
  id: number;
  // The list the field was created on — provenance, shown as the "from X"
  // hint. It is *not* what decides where the field applies: listIds is
  // (a field can even be removed from its own origin list). See ADR-0017.
  listId: number;
  // Every list this field appears on. One shared definition, many lists —
  // renaming it or changing its options changes it everywhere.
  listIds: number[];
  name: string;
  type: TaskFieldType;
  options: { name: string; color: string }[] | null; // only set for type "select"
  createdAt: string;
};

// The fields a given list actually has. The single place that answers
// "does this field apply here?", used by the task detail sheet (which
// fields to show) and the task table (which columns to offer) so the two
// can never disagree. listId null means "every list at once" — the master
// "All Tasks" pane, which spans them all.
export function fieldsForList(fields: TaskField[], listId: number | null): TaskField[] {
  if (listId === null) return fields;
  return fields.filter((f) => f.listIds.includes(listId));
}

// The inverse: everything defined elsewhere that this list could be given.
// Deliberately not "fields whose listId is another list" — a field's origin
// says nothing about where it currently applies (see TaskField.listId).
export function fieldsAddableToList(fields: TaskField[], listId: number): TaskField[] {
  return fields.filter((f) => !f.listIds.includes(listId));
}

// One row per (task, field) that actually has a value set, not every
// possible pair — see task_field_values' own schema comment. value is
// always plain text regardless of the field's type; the frontend interprets
// it against field.type the same way it interprets a status/tag color
// against the palette.
export type TaskFieldValue = {
  id: number;
  taskId: number;
  fieldId: number;
  value: string;
  updatedAt: string;
};

// Every piece of TaskTablePane's filter/sort/group state, bundled so it can
// be saved/restored as one named view. listFilter/statusFilter/
// priorityFilter/dueDateFilter are serialized as arrays (jsonb has no Set),
// converted to/from Set at the call site.
export type TaskViewFilters = {
  groupBy: "none" | "status" | "priority" | "dueDate" | "list";
  hideDone: boolean;
  hideRecurring: boolean;
  nameFilter: string;
  listFilter: number[];
  statusFilter: string[];
  priorityFilter: string[];
  dueDateFilter: string[];
  sortColumn: string | null;
  sortDirection: "asc" | "desc";
};

export type TaskView = {
  id: number;
  listId: number | null; // null = an "All Tasks" (master) view
  name: string;
  filters: TaskViewFilters;
  isDefault: boolean;
  createdAt: string;
};

const GROUP_BY_VALUES = new Set(["none", "status", "priority", "dueDate", "list"]);

// filters comes back from the API as opaque jsonb (Record<string, unknown>
// server-side — see task_views' own schema comment) — this is the one place
// that turns it back into a trusted TaskViewFilters, defensively, so a
// malformed or future-shape-changed saved view can't crash applyViewFilters
// with an unexpected type. Every field falls back to a safe empty/default
// value rather than throwing.
export function normalizeViewFilters(raw: unknown): TaskViewFilters {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const strArray = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
  const numArray = (v: unknown): number[] => (Array.isArray(v) ? v.filter((x): x is number => typeof x === "number") : []);
  return {
    groupBy: typeof r.groupBy === "string" && GROUP_BY_VALUES.has(r.groupBy) ? (r.groupBy as TaskViewFilters["groupBy"]) : "none",
    hideDone: r.hideDone === true,
    hideRecurring: r.hideRecurring === true,
    nameFilter: typeof r.nameFilter === "string" ? r.nameFilter : "",
    listFilter: numArray(r.listFilter),
    statusFilter: strArray(r.statusFilter),
    priorityFilter: strArray(r.priorityFilter),
    dueDateFilter: strArray(r.dueDateFilter),
    sortColumn: typeof r.sortColumn === "string" ? r.sortColumn : null,
    sortDirection: r.sortDirection === "desc" ? "desc" : "asc",
  };
}

// A custom status's identifier as stored in TaskItem.status — mirrors the
// backend's identical helper (task-statuses.ts). Never changes the meaning
// of the 4 built-in keys, so every existing `TASK_STATUSES.find(s => s.key
// === task.status)`-style lookup keeps working as long as callers merge in
// custom statuses via mergedStatuses() below rather than reading
// TASK_STATUSES alone.
export function customStatusKey(id: number): string {
  return `custom:${id}`;
}

// Built-ins + this user's custom statuses, in one lookup-able list — the
// shape every existing TASK_STATUSES consumer (StatusPill, StatusPicker,
// Kanban columns, sort, calendar, analytics) already expects.
//
// builtInColorOverrides recolors a built-in entry (key -> color) without
// touching its key/label — the 4 built-in keys stay a closed set (see
// customStatusKey's comment: "done" drives real logic like stamping
// completedAt, so it's never user-definable), only their color is. Optional
// with a safe empty-object default so existing call sites that don't pass it
// are unaffected.
export function mergedStatuses(
  customStatuses: TaskStatus[],
  builtInColorOverrides: Record<string, string> = {},
): { key: string; label: string; color: string }[] {
  return [
    ...TASK_STATUSES.map((s) => ({ ...s, color: builtInColorOverrides[s.key] ?? s.color })),
    ...customStatuses.map((s) => ({ key: customStatusKey(s.id), label: s.name, color: s.color })),
  ];
}

// One independent working period for a task — a task can have several (e.g.
// Monday morning, then again Wednesday afternoon), not a single embedded
// start/end pair. The calendar renders one entry per block.
export type TaskTimeBlock = {
  id: number;
  taskId: number;
  startDate: string;
  endDate: string;
  // Explicit, not inferred from startDate/endDate landing on exact day
  // boundaries — renders in the calendar's all-day strip regardless of the
  // period's exact times.
  isAllDay: boolean;
  createdAt: string;
};

// A fixed swatch palette — user-created tags pick one of these keys rather
// than arbitrary hex, so dark-mode contrast never needs runtime computation.
export const TAG_COLOR_KEYS = ["red", "orange", "amber", "yellow", "green", "teal", "blue", "purple", "pink", "gray"] as const;
export const TAG_COLOR_STYLES: Record<string, string> = {
  red: "bg-red-500/15 text-red-600 dark:text-red-400",
  orange: "bg-orange-500/15 text-orange-600 dark:text-orange-400",
  amber: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  yellow: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400",
  green: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  teal: "bg-teal-500/15 text-teal-600 dark:text-teal-400",
  blue: "bg-primary/10 text-primary",
  purple: "bg-purple-500/15 text-purple-600 dark:text-purple-400",
  pink: "bg-pink-500/15 text-pink-600 dark:text-pink-400",
  gray: "bg-muted text-muted-foreground",
};
export const TAG_SWATCH_STYLES: Record<string, string> = {
  red: "bg-red-500", orange: "bg-orange-500", amber: "bg-amber-500", yellow: "bg-yellow-500",
  green: "bg-emerald-500", teal: "bg-teal-500", blue: "bg-primary", purple: "bg-purple-500",
  pink: "bg-pink-500", gray: "bg-muted-foreground",
};
// border- variant of the same palette — the task-row "mark done" circle
// outlines in the task's current status color (not just a flat neutral
// border), same colors TaskStatusDot/TAG_SWATCH_STYLES already use.
export const TAG_BORDER_STYLES: Record<string, string> = {
  red: "border-red-500", orange: "border-orange-500", amber: "border-amber-500", yellow: "border-yellow-500",
  green: "border-emerald-500", teal: "border-teal-500", blue: "border-primary", purple: "border-purple-500",
  pink: "border-pink-500", gray: "border-muted-foreground",
};

// A fixed status set (unlike priority, status drives real logic — "done"
// stamps completedAt — so it stays a closed set rather than user-definable),
// reusing the same color palette as priority tags so both read consistently.
export const TASK_STATUSES: { key: string; label: string; color: string }[] = [
  { key: "todo", label: "To Do", color: "gray" },
  { key: "in_progress", label: "In Progress", color: "blue" },
  { key: "blocked", label: "Blocked", color: "red" },
  { key: "done", label: "Done", color: "green" },
];

export const SORT_LABELS: Record<SortMode, string> = {
  newest: "Newest first",
  oldest: "Oldest first",
  unread: "Unread first",
  priority: "Priority",
};

export const PRIORITY_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 };

export type GroupedEntry =
  | { kind: "msg"; msg: Message; grouped: boolean }
  | { kind: "day"; label: string };
